import { useState } from 'react';
import dayjs from 'dayjs';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { clientRoute } from '@/features/dashboard/clients/routes/clientRoute.ts';
import useGetClientBookingDetails from '@/features/dashboard/clients/hooks/useGetClientBookingDetails.ts';
import { DetailsMode } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { clientTabLinks } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';
import { initialsOf } from '@/utils/personName.ts';
import { pluralForm } from '@/utils/pluralize.ts';
import { toChangePackageStatus } from '@/features/dashboard/bookingChanges/types/BookingChangeDetailDto.mapper.ts';
import { DetailsProvider } from '@/features/dashboard/bookingChanges/contexts/DetailsContext.ts';
import useClientBillView from '@/features/dashboard/bookingChanges/hooks/useClientBillView.ts';
import useChangeDraft from '@/features/dashboard/bookingChanges/hooks/useChangeDraft.ts';
import useGetBookingSummary from '@/features/dashboard/bookingChanges/hooks/useGetBookingSummary.ts';
import Chip from '@/components/ui/Chip.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { getBookingStatusLabel } from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import { bookingStatusTone } from '@/features/dashboard/bookingChanges/components/bookingDetails/bookingHeaderPresentation.ts';
import TagPickerRow from '@/features/dashboard/tags/components/TagPickerRow.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import {
  DetailsHeader,
  DetailsLayout,
  useIsTabMenu,
} from '@/components/details';
import { ChangeDraftBanner } from '@/features/dashboard/bookingChanges/components/layout';
import {
  EditFieldModalHost,
  EditModeToggle,
  ExitEditingModal,
  useEditField,
  useExitEditing,
} from '@/features/dashboard/bookingChanges/components/editing';
import { DetailsSidebar } from '@/features/dashboard/bookingChanges/components/sidebar';
import BookingActionsMenu from '@/features/dashboard/bookingChanges/components/bookingActions/BookingActionsMenu.tsx';
import TotalBar from '@/components/ui/TotalBar.tsx';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import { pluralizeItems } from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/**
 * Szkielet ekranu osoby zapisującej (B1/B2): tożsamość płatnika, panel boczny,
 * belka wartości zmian i modale. Treść zakładki wstawia router przez
 * `<Outlet />` — „Informacje” i „Rezerwacja” to osobne route'y.
 */
const ClientDetailsPage = () => {
  const { clientId } = clientRoute.useParams();
  const { client, bookingId, billClientId } = clientRoute.useLoaderData();
  const search = clientRoute.useSearch();
  const navigate = useNavigate();
  const [onlyChanged, setOnlyChanged] = useState(false);

  // TODO: clients support one tour today — revisit when multi-tour clients land
  const tourId = client.tourIds[0];
  const bookingDetailsQuery = useGetClientBookingDetails(clientId, tourId);
  const bookingCancelled =
    bookingDetailsQuery.data?.status === BookingStatus.CANCELLED;

  const { view } = useClientBillView(bookingId);
  const { draft, change } = useChangeDraft(bookingId);

  /*
   * Tryb edycji wymaga szkicu, więc nie wystarczy `?mode=edit` w adresie:
   * pod ten adres da się wejść linkiem, odświeżeniem strony albo cofnięciem
   * po odrzuceniu paczki. Ekran wyglądał wtedy na gotowy do edycji, a każdy
   * zapis pozycji cicho przepadał, bo nie miał do czego trafić.
   *
   * Warunek na szkicu zamiast przekierowania jest odporny na wyścig: między
   * założeniem paczki a jej pojawieniem się w zapytaniu ekran po prostu
   * jeszcze nie pokazuje edycji, zamiast odbijać się do podglądu.
   */
  const isEditing =
    search.mode === DetailsMode.EDIT && draft !== null && !bookingCancelled;
  const summaryQuery = useGetBookingSummary(bookingId);

  /** Zmiana trybu zostaje na bieżącej zakładce — `.` to aktualna ścieżka. */
  const goToMode = (mode: DetailsMode) =>
    navigate({ to: '.', search: { mode } });

  const exitToView = () => goToMode(DetailsMode.VIEW);

  const exitEditing = useExitEditing({
    bookingId,
    draft,
    draftStatus: change ? toChangePackageStatus(change.status) : null,
    onEnterEditing: () => goToMode(DetailsMode.EDIT),
    onExitToView: exitToView,
  });

  const editField = useEditField(bookingId);

  const openBooking = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
    });

  /** „Cała paczka ↗” — ten sam szkic z poziomu całej rezerwacji. */
  const openBookingInEditMode = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
      search: { mode: DetailsMode.EDIT },
    });

  const clientSinceYear = bookingDetailsQuery.data
    ? dayjs(bookingDetailsQuery.data.registeredAt).year()
    : undefined;

  // Orzeczenie dobiera się do liczby tak samo jak w modalu wyjścia z edycji:
  // „1 pozycja dotyczy”, „2 pozycje dotyczą”, „5 pozycji dotyczy”.
  const localDraftItemCount = view?.draftItemCount ?? 0;
  const draftScopeLabel = draft
    ? `${pluralizeItems(localDraftItemCount)} ${pluralForm(
        localDraftItemCount,
        'dotyczy',
        'dotyczą',
        'dotyczy',
      )} tej osoby, ${draft.itemCount} w całej rezerwacji`
    : undefined;

  /** Goły adres zapisującego: menu kafli na telefonie, „Informacje” szeroko. */
  const menuLink = {
    to: '/app/clients/$clientId',
    params: { clientId },
    search,
  } as const;

  /*
   * Na menu ekran ma być samym nagłówkiem i wyborem zakładki — panel boczny
   * i belka wartości zmian należą do treści, a treści tu jeszcze nie ma.
   */
  const isTabMenu = useIsTabMenu(menuLink);

  const header = (
    <DetailsHeader
      initials={initialsOf(client.firstName, client.lastName)}
      avatarTone="dark"
      title={`${client.firstName} ${client.lastName}`}
      badges={[
        <Chip label="Płatnik" tone="neutral" fill="soft" />,
        ...(bookingCancelled
          ? [
              <StatusDot
                className="text-[15px]"
                tone={bookingStatusTone[BookingStatus.CANCELLED]}
                label={getBookingStatusLabel(BookingStatus.CANCELLED)}
              />,
            ]
          : []),
      ]}
      meta={[
        `${client.email} · ${client.phoneNumber.dialCode} ${client.phoneNumber.number}`,
        ...(clientSinceYear ? [`Klient od ${clientSinceYear}`] : []),
      ]}
      tabs={clientTabLinks(clientId, search)}
      menuLink={menuLink}
      tags={
        <TagPickerRow targetType={TaggableType.CLIENT} targetId={clientId} />
      }
      highlighted={isEditing}
      actions={
        <div className="flex items-center gap-3">
          {!bookingCancelled && (
            <EditModeToggle
              isEditing={isEditing}
              availability={exitEditing.availability}
              onToggle={exitEditing.toggleEditing}
            />
          )}
          {/* Bez rezygnacji — ta dotyczy uczestników; poza tym te same zasady
              co na ekranie rezerwacji i uczestnika. */}
          {summaryQuery.data && !isEditing && (
            <BookingActionsMenu clientId={summaryQuery.data.clientId} />
          )}
        </div>
      }
      banner={
        draft && (
          <ChangeDraftBanner
            draft={draft}
            isEditing={isEditing}
            scopeLabel={draftScopeLabel}
            editLock={exitEditing.availability}
            onResumeEditing={() => exitEditing.toggleEditing(true)}
            onWithdrawPackage={exitEditing.withdrawPackage}
            isWithdrawing={exitEditing.isDiscarding}
            onOpenFullPackage={openBookingInEditMode}
          />
        )
      }
    />
  );

  return (
    <DetailsProvider
      value={{
        bookingId,
        bookingCode: summaryQuery.data?.bookingCode ?? '',
        isEditing,
        onlyChanged,
        onOnlyChangedChange: setOnlyChanged,
        editField,
        currentPersonId: billClientId,
        onOpenBooking: openBooking,
      }}
    >
      <DetailsLayout
        header={header}
        sidebar={
          isTabMenu ? null : (
            <DetailsSidebar
              bookingId={bookingId}
              isEditing={isEditing}
              currentPersonId={billClientId}
              localDraftItemCount={view?.draftItemCount}
              onExitEditing={exitToView}
            />
          )
        }
        bottomBar={
          isEditing && draft && !isTabMenu ? (
            <TotalBar
              label="Wartość zmian"
              value={
                <MoneyAmountList values={draft.totalDelta} variant="signed" />
              }
            />
          ) : null
        }
      >
        <Outlet />

        {exitEditing.isExitModalOpen && draft && (
          <ExitEditingModal
            draft={draft}
            isDiscarding={exitEditing.isDiscarding}
            onChoice={exitEditing.resolveExit}
            onDismiss={exitEditing.dismissExit}
          />
        )}

        <EditFieldModalHost editField={editField} />
      </DetailsLayout>
    </DetailsProvider>
  );
};

export default ClientDetailsPage;
